import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, ChevronDown, Gift, Sparkles, MapPin, Star, X } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const sizeClass = size === "sm" ? "w-8 h-8 text-xs" : size === "lg" ? "w-14 h-14 text-xl" : "w-10 h-10 text-sm";
  return (
    <div className={`${sizeClass} rounded-xl ${colors[i]} flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span>
    </div>
  );
};

const QuickTag = ({ label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
      active
        ? "bg-slate-900 text-white shadow-lg"
        : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"
    }`}
    data-testid={testId}
  >
    {label}
  </button>
);

export const MinimalistView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, bestValue, stats, favoriteIds, toggleFavorite, categories, selectedCategory, setSelectedCategory, searchQuery, setSearchQuery } = data;
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const totalValue = offers.reduce((sum, o) => sum + (o.offer_value_est || 0), 0);

  // Popular tags from categories
  const popularTags = categories.filter(c => c !== "All").slice(0, 8);

  // Show results when searching or category selected
  const showResults = searchQuery.length > 0 || selectedCategory.length > 0 || focused;

  return (
    <div className="min-h-screen bg-white pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Centered hero */}
      <div className="flex flex-col items-center justify-center px-8 pt-16 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-slate-400 text-sm mb-2">{stats.days_until_birthday} days to your birthday</p>
          <h1
            className="text-4xl font-extrabold text-slate-900 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            What sounds good<br />
            <span className="text-sky-500">today</span>?
          </h1>
        </motion.div>

        {/* Big search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full mt-8"
        >
          <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border-2 transition-colors ${
            focused ? "border-sky-500 shadow-lg shadow-sky-500/10" : "border-slate-200"
          }`}>
            <Search className={`w-5 h-5 transition-colors ${focused ? "text-sky-500" : "text-slate-300"}`} />
            <input
              ref={inputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              placeholder="Coffee, burgers, Sephora..."
              className="flex-1 text-base outline-none text-slate-800 placeholder-slate-300"
              data-testid="minimalist-search"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setFocused(false); }} className="text-slate-300 hover:text-slate-500">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Quick tags */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-2 mt-6"
        >
          {popularTags.map(tag => (
            <QuickTag
              key={tag}
              label={tag}
              active={selectedCategory.includes(tag)}
              onClick={() => {
                setSelectedCategory(selectedCategory.includes(tag) ? selectedCategory.filter(c => c !== tag) : [tag]);
                setFocused(true);
              }}
              testId={`minimalist-tag-${tag.toLowerCase().replace(/\s/g, '-')}`}
            />
          ))}
        </motion.div>
      </div>

      {/* Subtle stats line */}
      {!showResults && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="px-8 text-center mb-8"
        >
          <div className="inline-flex items-center gap-6 text-sm text-slate-400">
            <span><strong className="text-slate-700">{offers.length}</strong> deals</span>
            <span className="w-1 h-1 bg-slate-300 rounded-full" />
            <span><strong className="text-green-600">${totalValue.toFixed(0)}</strong> total value</span>
            <span className="w-1 h-1 bg-slate-300 rounded-full" />
            <span><strong className="text-sky-500">{bestValue.length}</strong> best picks</span>
          </div>
        </motion.div>
      )}

      {/* Suggestions — shown when idle */}
      {!showResults && bestValue.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="px-8"
        >
          <p className="text-xs font-medium text-slate-400 tracking-widest uppercase mb-4">Top Picks for You</p>
          <div className="space-y-3">
            {bestValue.slice(0, 5).map((offer, i) => (
              <motion.div
                key={offer.business_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + i * 0.08 }}
                onClick={() => onOfferClick(offer)}
                className="flex items-center gap-4 py-3 cursor-pointer group"
                data-testid={`minimalist-pick-${offer.business_id}`}
              >
                <MiniLogo name={offer.name} size="lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">{offer.name}</p>
                  {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                  <p className="text-sm text-slate-400 truncate">{offer.offer_title}</p>
                </div>
                <span className="text-lg font-bold text-green-600">${offer.offer_value_est}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Results — slide up when searching */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            className="px-8"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">{offers.length} results</p>
              {selectedCategory.length > 0 && (
                <button
                  onClick={() => { setSelectedCategory([]); setSearchQuery(""); setFocused(false); }}
                  className="text-xs text-sky-500 font-medium"
                  data-testid="minimalist-clear"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="space-y-2">
              {offers.slice(0, 20).map((offer, i) => (
                <motion.div
                  key={offer.business_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => onOfferClick(offer)}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-100"
                  data-testid={`minimalist-result-${offer.business_id}`}
                >
                  <MiniLogo name={offer.name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{offer.name}</p>
                    <p className="text-xs text-slate-400 truncate">{offer.offer_title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400">{offer.category}</span>
                      {offer.ease_stars >= 4 && (
                        <span className="text-[10px] text-amber-500 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" /> Easy</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }}
                      className="p-0.5"
                    >
                      <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-200"}`} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
